import React, { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { Avatar, type ComboboxItem } from '@node-space/ui-components'
import { useGetCustomersWalletProfileQuery } from 'hooks/queries/Customers/useCustomerWalletProfilesQuery'

const SUPPORTED_FIAT_CURRENCIES = ['EUR', 'GBP', 'USD']

const buildCurrencyOption = (code: string): ComboboxItem => ({
  value: code,
  leftText: code,
  LeftElement: <Avatar name={code.toLowerCase()} size="small" />,
})

const ALL_CURRENCIES_VALUE = ''

export const useCurrencyFilterOptions = (customerReference?: string) => {
  const { t } = useTranslation()
  const { data: walletProfiles, isFetching: isFetchingCurrencies } =
    useGetCustomersWalletProfileQuery(
      customerReference ? { customerId: customerReference } : undefined
    )

  const profileCurrencies = useMemo(() => {
    if (!walletProfiles?.profiles) return { allCurrencies: [] }

    const allCurrencies = new Set<string>()

    walletProfiles.profiles.forEach(profile => {
      profile.currencies?.forEach(currency => {
        allCurrencies.add(currency)
      })
    })

    return {
      allCurrencies: Array.from(allCurrencies).sort((a, b) => a.localeCompare(b)),
    }
  }, [walletProfiles])

  const currencyOptions: ComboboxItem[] = useMemo(() => {
    const allCurrenciesOption: ComboboxItem = {
      value: ALL_CURRENCIES_VALUE,
      leftText: t('customers.allCurrencies'),
    }

    const currencies = walletProfiles?.profiles?.length
      ? profileCurrencies.allCurrencies
      : SUPPORTED_FIAT_CURRENCIES

    return [allCurrenciesOption, ...currencies.map(buildCurrencyOption)]
  }, [profileCurrencies, walletProfiles, t])

  return { currencyOptions, isFetchingCurrencies }
}
